import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  Info,
} from '@mui/icons-material';

const UserGuide: React.FC = () => {
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        User Guide
      </Typography>

      {/* Video Tutorial */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>📹 Video Tutorial</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{
            position: 'relative',
            paddingBottom: '56.25%',
            height: 0,
            overflow: 'hidden',
            borderRadius: 2
          }}>
            <iframe
              src="https://www.loom.com/embed/1e97de3f9b10432997db60364e7835bc?sid=7d5e8f3e-4b0f-4b5a-9c5a-2e3f8e7d9e8f"
              frameBorder="0"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            />
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Watch this complete walkthrough demonstrating: file upload, settings configuration,
              processing workflow, and interpreting the analysis results.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Network Requirements - Subtle notice */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          <strong>Network Tip:</strong> For best results, use WiFi when uploading large files. Cellular data may timeout during uploads.
        </Typography>
      </Box>

      {/* What is the Complete Mastering System? */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Complete Mastering System</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            <strong>This is a complete mastering system that includes:</strong>
          </Typography>
          <Stack spacing={1} sx={{ ml: 2 }}>
            <Typography variant="body2">• <strong>De-essing:</strong> Remove harshness before processing</Typography>
            <Typography variant="body2">• <strong>Pre-Processing Frequency EQ (STFT Linear Phase):</strong> 7-band mastering-grade EQ preparation</Typography>
            <Typography variant="body2">• <strong>EQ & Level Matching:</strong> Core algorithm matching frequency response and loudness to reference</Typography>
            <Typography variant="body2">• <strong>Genre Presets & Advanced Settings:</strong> Optimized parameters for different music styles</Typography>
            <Typography variant="body2">• <strong>Transient Preservation:</strong> Maintain punch and attack characteristics</Typography>
            <Typography variant="body2">• <strong>Dithering:</strong> Professional noise-shaping for bit-depth conversion</Typography>
            <Typography variant="body2">• <strong>Deep LUFS & Frequency Analysis:</strong> Professional loudness and spectral analysis</Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* How to Use */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>How to Use</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                1. Upload Your Unmastered Track
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This is your mix that you want to master. It should be your final mix with all
                tracks balanced, but without mastering processing applied.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                2. Upload Reference Track(s)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a professionally mastered track in the same genre as your unmastered track.
                This track should have the sonic characteristics you want to match.
              </Typography>
              <Alert severity="warning" sx={{ mt: 1, fontSize: '0.85rem' }}>
                <Typography variant="caption" fontWeight={600}>Recommended: 30 seconds maximum</Typography>
                <Typography variant="caption" display="block">
                  For best results, use a 30-second excerpt from the reference track's chorus or
                  most dynamic section. Shorter references process faster and are often more effective.
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                You can optionally upload a second reference and blend them together using the slider.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                3. Configure Settings (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a preset or customize settings. See "Understanding Presets" below for details.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                4. Click "Master Audio"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Processing typically takes 4-5 minutes depending on file size. You'll see real-time
                progress updates.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                5. Review & Download
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Preview your mastered track with the built-in audio player and peak meters.
                Compare the frequency spectrum and loudness metrics against the reference.
                Download when satisfied.
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Understanding Presets */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Understanding Presets</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Default */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="Default" color="primary" size="small" />
                <Typography variant="subtitle2" fontWeight={600}>Balanced Mastering</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Best for most use cases. Applies moderate limiting and normalization for a
                polished, professional sound suitable for streaming platforms.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                Settings: PCM24, Limiter On, Normalize On
              </Typography>
            </Paper>

            {/* Apple Digital Masters */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="Apple Digital Masters" color="success" size="small" />
                <Typography variant="subtitle2" fontWeight={600}>Apple Music Optimized</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Meets Apple's Digital Masters technical specifications. Uses true peak limiting
                at -1dB TP with 96kHz/24-bit output. Ideal for Apple Music distribution.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                Settings: PCM24 96kHz, Limiter On (-1dB TP), Normalize On
              </Typography>
            </Paper>

            {/* Streaming Loud */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="Streaming Loud" color="warning" size="small" />
                <Typography variant="subtitle2" fontWeight={600}>Maximum Loudness</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Maximizes loudness for competitive streaming. Applies aggressive limiting while
                maintaining quality. Good for EDM, pop, and commercial music.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                Settings: PCM24, Limiter On (Aggressive), Normalize On
              </Typography>
            </Paper>

            {/* Conservative */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="Conservative" color="info" size="small" />
                <Typography variant="subtitle2" fontWeight={600}>Preserve Dynamics</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Gentle mastering that preserves more of the original dynamics. Uses soft limiting
                and moderate normalization. Ideal for acoustic, classical, or jazz music.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                Settings: PCM24, Limiter On (Soft), Normalize Off
              </Typography>
            </Paper>

            {/* High-Res */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="High-Res" color="secondary" size="small" />
                <Typography variant="subtitle2" fontWeight={600}>Audiophile Quality</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Maximum quality output for hi-fi systems and audiophile listeners. Uses 32-bit float
                format with minimal processing. Best for high-resolution audio distribution.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                Settings: PCM32, Limiter Off, Normalize Off
              </Typography>
            </Paper>
          </Stack>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Genre Presets Explained</Typography>
            <Typography variant="body2">
              Each genre preset automatically adjusts multiple parameters based on typical mastering practices
              for that genre. This includes limiter threshold, normalization settings, and frequency weighting.
              You can use these as starting points and further customize in the Advanced Settings section.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Advanced Settings Explained */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Advanced Settings Explained</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                Output Level & Internal Limiter
              </Typography>
              <Typography variant="body2" paragraph>
                The <strong>Internal Limiter</strong> controls whether the algorithm applies brick-wall
                limiting after EQ matching. The <strong>Output Level</strong> slider adjusts the final
                gain before output.
              </Typography>
              <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                <Typography variant="body2">
                  For maximum control, you can disable the limiter and normalize, allowing you to apply
                  your own limiting chain in your DAW for a more customized sound.
                </Typography>
              </Alert>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                EQ Smoothing (LOWESS)
              </Typography>
              <Typography variant="body2">
                Controls how smooth or sharp the EQ matching curve will be:
              </Typography>
              <Stack spacing={1} sx={{ mt: 1, ml: 2 }}>
                <Typography variant="body2">
                  • <strong>Lower values (0.01-0.03):</strong> Sharp, precise EQ matching.
                  Follows reference more closely but may introduce artifacts.
                </Typography>
                <Typography variant="body2">
                  • <strong>Default (0.05):</strong> Balanced approach - smooth yet effective.
                </Typography>
                <Typography variant="body2">
                  • <strong>Higher values (0.08-0.15):</strong> Very smooth, gentle EQ curves.
                  More musical but less precise matching.
                </Typography>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                Sample Rate
              </Typography>
              <Typography variant="body2">
                Internal processing sample rate. Higher rates preserve more high-frequency detail
                but require more processing time:
              </Typography>
              <Stack spacing={1} sx={{ mt: 1, ml: 2 }}>
                <Typography variant="body2">
                  • <strong>44.1 kHz:</strong> CD quality, fastest processing
                </Typography>
                <Typography variant="body2">
                  • <strong>48 kHz:</strong> Professional standard (recommended)
                </Typography>
                <Typography variant="body2">
                  • <strong>96 kHz:</strong> High-resolution, slower but maximum quality
                </Typography>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                FFT Size
              </Typography>
              <Typography variant="body2">
                Controls frequency analysis precision:
              </Typography>
              <Stack spacing={1} sx={{ mt: 1, ml: 2 }}>
                <Typography variant="body2">
                  • <strong>2048:</strong> Fast processing, less detail
                </Typography>
                <Typography variant="body2">
                  • <strong>4096:</strong> Balanced (default)
                </Typography>
                <Typography variant="body2">
                  • <strong>8192:</strong> Maximum detail, slower processing
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Pre-Processing: De-esser Explained */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Pre-Processing: De-esser Explained</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                What is De-essing?
              </Typography>
              <Typography variant="body2" paragraph>
                De-essing is a pre-processing technique that reduces harsh, sibilant frequencies (the "S" and "T" sounds)
                in vocals or other bright instruments before mastering. This prevents these frequencies from being
                over-emphasized during the EQ matching process.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                How It Works: Technical Details
              </Typography>
              <Typography variant="body2" paragraph>
                The de-esser uses a sophisticated multi-stage algorithm to intelligently reduce harshness:
              </Typography>

              <Stack spacing={1.5} sx={{ ml: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>1. Bandpass Filter Isolation</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    A 4th-order Butterworth bandpass filter (Q=1.5) isolates the sibilant frequency range.
                    The frequency range is calculated dynamically based on your selected center frequency:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4, fontFamily: 'monospace', mt: 0.5 }}>
                    Range = Center Frequency ± (Center Frequency / 3)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mt: 0.5 }}>
                    Example: 7000 Hz center → 4667-9333 Hz range
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    The range is constrained between 3000 Hz (minimum) and 12000 Hz (maximum).
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={600}>2. Envelope Detection with Attack/Release</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    An envelope follower tracks the energy in the sibilant band with:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    • <strong>Attack:</strong> 1 ms (fast response to sibilance)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                    • <strong>Release:</strong> 50 ms (smooth recovery)
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={600}>3. Dynamic Threshold Detection</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    The threshold is calculated automatically based on the RMS level of the sibilant band
                    (threshold = RMS × 2.0), ensuring adaptive processing that works with different recording levels.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={600}>4. Gain Reduction (3:1 Compression Ratio)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    When sibilance exceeds the threshold, a 3:1 compression ratio is applied, scaled by your
                    amount setting (0-100%). The processed sibilant band is then mixed back with the original signal.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                Parameters
              </Typography>
              <Stack spacing={1} sx={{ ml: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>Amount (0-100%)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Controls the intensity of de-essing. Higher values apply more reduction.
                    Start with 50% and adjust based on your needs.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>Frequency (4000-10000 Hz)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Center frequency of the sibilant range. Default is 7000 Hz, which works for most vocals.
                    For darker vocals, try 4000-5000 Hz. For brighter sources, try 8000-9000 Hz.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                When to Use De-essing
              </Typography>
              <Stack spacing={1} sx={{ ml: 2 }}>
                <Typography variant="body2">
                  • <strong>Bright vocal mixes:</strong> When vocals have excessive sibilance or harshness
                </Typography>
                <Typography variant="body2">
                  • <strong>Before mastering:</strong> De-essing as pre-processing prevents the EQ matching algorithm
                  from amplifying harsh frequencies
                </Typography>
                <Typography variant="body2">
                  • <strong>High-frequency energy:</strong> If your mix has too much presence or air that becomes
                  fatiguing
                </Typography>
              </Stack>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Pro Tip</Typography>
              <Typography variant="body2">
                De-essing is applied <strong>before</strong> the EQ matching algorithm processes your track.
                This ensures a cleaner starting point and prevents harsh frequencies from being over-emphasized
                during mastering. If your mix already sounds well-balanced, you may not need de-essing.
              </Typography>
            </Alert>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Transient Preservation */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Transient Preservation</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2">
              Transient Preservation is a post-processing feature that blends the original drum hits,
              vocal attacks, and other transient material back into the mastered output. This helps
              maintain the "punch" and dynamics that can sometimes be smoothed out during mastering.
            </Typography>

            <Alert severity="warning">
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Important: Use Sparingly (0-30% Maximum)
              </Typography>
              <Typography variant="body2">
                Transient preservation blends <strong>unmastered</strong> audio back into your
                mastered output. Higher percentages reduce the effectiveness of mastering and can
                introduce artifacts. For best mastering results, start at <strong>0%</strong> (off).
              </Typography>
            </Alert>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                How It Works
              </Typography>
              <Stack spacing={1} sx={{ ml: 2 }}>
                <Typography variant="body2">
                  <strong>1. Transient Detection:</strong> The algorithm analyzes your original track
                  to identify sharp increases in energy (transients) such as drum hits, plucks, and vocal attacks.
                </Typography>
                <Typography variant="body2">
                  <strong>2. Sample Rate Alignment:</strong> If your original track is at a different
                  sample rate (e.g., 44.1kHz) than the mastered output (e.g., 48kHz), the original is
                  resampled to match, preventing timing artifacts.
                </Typography>
                <Typography variant="body2">
                  <strong>3. Dynamic Blending:</strong> During detected transients, more of the original
                  signal is blended in. During sustained notes, more of the mastered signal is used.
                </Typography>
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                Understanding the Percentages
              </Typography>
              <Stack spacing={1} sx={{ ml: 2 }}>
                <Typography variant="body2">
                  • <strong>0% (Recommended):</strong> Fully mastered output. Best for achieving
                  professional mastering results.
                </Typography>
                <Typography variant="body2">
                  • <strong>10-20%:</strong> Subtle transient preservation. Use only if you need to
                  add back a small amount of punch to specific hits.
                </Typography>
                <Typography variant="body2">
                  • <strong>30% (Maximum):</strong> Maximum recommended blend. Higher values significantly
                  reduce mastering effectiveness.
                </Typography>
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                When to Use Transient Preservation
              </Typography>
              <Stack spacing={1} sx={{ ml: 2 }}>
                <Typography variant="body2">
                  • <strong>Drum-heavy tracks:</strong> If mastering makes your drums too smooth and you
                  need to restore some attack
                </Typography>
                <Typography variant="body2">
                  • <strong>Vocal clarity:</strong> When vocal consonants lose definition after mastering
                </Typography>
                <Typography variant="body2">
                  • <strong>Plucked instruments:</strong> When guitar picks, bass plucks, or piano attacks
                  become too rounded
                </Typography>
              </Stack>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Pro Tip</Typography>
              <Typography variant="body2">
                Transient preservation is applied <strong>after</strong> mastering completes. It works
                by mixing unmastered transients back into the final output, which inherently reduces
                the impact of mastering. For professional results, try mastering without transient
                preservation first (0%). Only add it if you specifically need more punch, and use the
                lowest percentage that achieves your goal (typically 10-15%).
              </Typography>
            </Alert>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Tips & Best Practices */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Tips & Best Practices</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 20 }}>•</Typography>
              <Typography variant="body2">
                <strong>Leave headroom:</strong> Ensure your target mix peaks around -6dB to -3dB
                to give the EQ matching algorithm room to work.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 20 }}>•</Typography>
              <Typography variant="body2">
                <strong>Match genres:</strong> Choose reference tracks from the same genre as your
                target for best results.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 20 }}>•</Typography>
              <Typography variant="body2">
                <strong>Use high-quality references:</strong> The better your reference track sounds,
                the better your result will be.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 20 }}>•</Typography>
              <Typography variant="body2">
                <strong>Experiment with presets:</strong> Try different presets to find the sound
                that works best for your track.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 20 }}>•</Typography>
              <Typography variant="body2">
                <strong>Use WiFi for uploads:</strong> Large audio files may timeout on cellular
                connections. WiFi is more reliable.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 20 }}>•</Typography>
              <Typography variant="body2">
                <strong>Trim reference tracks:</strong> Use 30-second excerpts from your reference
                tracks for faster processing and more focused results.
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Understanding the Results */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Understanding the Results</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Loudness Meters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Shows LUFS (Loudness Units Full Scale) measurements for your target, reference,
                and mastered output. This helps you understand how the loudness compares.
                Most streaming platforms target around -14 LUFS.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Frequency Response Chart
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualizes the tonal balance of your tracks across the frequency spectrum.
                The mastered output (green) shows how the EQ matching algorithm adjusted the EQ to match
                the reference's frequency curve.
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Peak Meter
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Real-time peak level display when playing audio. Green is safe, yellow is
                getting loud, orange is very loud, and red indicates clipping (should be avoided).
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Supported Formats */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" fontWeight={600}>Supported Formats</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            <strong>Input formats:</strong> WAV, AIFF, FLAC, MP3, OGG, M4A
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Output formats:</strong>
          </Typography>
          <Stack spacing={1} sx={{ ml: 2 }}>
            <Typography variant="body2">• PCM16 - 16-bit (CD quality)</Typography>
            <Typography variant="body2">• PCM24 - 24-bit (Recommended)</Typography>
            <Typography variant="body2">• PCM32 - 32-bit float (Highest quality)</Typography>
          </Stack>
          <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
            <Typography variant="caption">
              For best results, use lossless formats (WAV, AIFF, FLAC) for both target and reference tracks.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default UserGuide;
